/***
 *
 *   SCREENING ROW HELPERS
 *   Pure display logic for manual screening rows: verification rows created on
 *   the Screening page (/screening), whose type is "sanctions",
 *   "adverse_media" or "sanctions+adverse_media". They have no Veriff session,
 *   so result_info is null and the subject's name lives on first_name /
 *   last_name instead.
 *
 *   Client mirror of server rules. The client cannot import from outside
 *   client/src, so each rule is restated here:
 *   - isScreeningRow: helper/screening.js isScreeningOnly
 *   - canDownloadScreeningPdf: helper/screening.js screeningReportRefusal
 *   - screeningPdfFilename: helper/screeningReportPdf.js
 *     buildScreeningReportFilename (the blob download ignores
 *     Content-Disposition, so the browser names the file itself)
 *
 *   Keep this file import-free: test/screening-report-pdf.js loads it on the
 *   server and compares the last two rules with the server's.
 *
 **********/

const SCREENING_CHECKS = ["sanctions", "adverse_media"];

/**
 * True when every "+" token of `type` is a screening check. Whole tokens only,
 * so "sanctions_monitoring" and "kyc+sanctions" are not screening rows, and an
 * empty token ("" or "sanctions+") fails.
 */
export function isScreeningRow(type) {
  if (typeof type !== "string") return false;
  return type.split("+").every((token) => SCREENING_CHECKS.includes(token));
}

// "YYYY-MM-DD" → "dd/mm/yyyy". The server validates the DOB before storing it,
// so anything else is shown as stored rather than hidden.
function formatDob(iso) {
  if (typeof iso !== "string" || iso === "") return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  return match ? `${match[3]}/${match[2]}/${match[1]}` : iso;
}

/**
 * "Screened: {first_name} {last_name}", and with `withDob` the DOB the
 * PEP/Sanctions search was narrowed by: " · DOB dd/mm/yyyy", or
 * " · DOB not provided" when none was entered. When a middle name made the
 * server also search without it (sanctions_results.names_searched holds
 * exactly two strings), `withDob` adds " · also searched as {second name}".
 */
export function formatScreenedHeader(row, options) {
  const withDob = options?.withDob === true;
  const name = `${row?.first_name || ""} ${row?.last_name || ""}`.trim();
  const header = `Screened: ${name}`;
  if (!withDob) return header;

  const dob = formatDob(row?.sanctions_results?.dob);
  const dobHeader = `${header} · DOB ${dob || "not provided"}`;
  const names = row?.sanctions_results?.names_searched;
  const searchedBothWays =
    Array.isArray(names) &&
    names.length === 2 &&
    names.every((searched) => typeof searched === "string");
  return searchedBothWays
    ? `${dobHeader} · also searched as ${names[1]}`
    : dobHeader;
}

// The success tests the server applies to each provider before it stores a
// screening row, so a row that fails them has no report to download.
function hasStoredResults(row, check) {
  if (check === "sanctions") {
    return Array.isArray(row.sanctions_results?.found_records);
  }
  const node = row.adverse_media_results?.AML?.[0];
  return (
    !!node &&
    (node.status?.serviceSuspected === false || Array.isArray(node.data))
  );
}

/**
 * True when the server will build the screening report for this check of
 * this row: true exactly when screeningReportRefusal returns null. KYC rows,
 * kyc+sanctions included, keep their KYC-tab Download PDF instead.
 */
export function canDownloadScreeningPdf(row, check) {
  if (typeof check !== "string" || !SCREENING_CHECKS.includes(check)) {
    return false;
  }
  if (!isScreeningRow(row?.type)) return false;
  if (!row.type.split("+").includes(check)) return false;
  return hasStoredResults(row, check);
}

const FILENAME_PREFIXES = {
  sanctions: "pep-sanctions-screening",
  adverse_media: "adverse-media-screening",
};
const FILENAME_MAX_LENGTH = 80;

// helper/uboReportPdf.js sanitiseFilenamePart: [A-Za-z0-9._-] only, capped.
function sanitiseFilenamePart(value) {
  return String(value == null ? "" : value)
    .replace(/[\r\n]+/g, " ")
    .trim()
    .replace(/\s+/g, "-")
    .replace(/[^A-Za-z0-9._-]/g, "")
    .replace(/^[-.]+|[-.]+$/g, "")
    .slice(0, FILENAME_MAX_LENGTH)
    .replace(/[-.]+$/, "");
}

// A Date, a non-empty date string or a finite epoch number; anything else
// (null included, which `new Date` reads as 1970) is no date.
function toValidDate(value) {
  let date = null;
  if (value instanceof Date) date = value;
  else if (typeof value === "string" && value !== "") date = new Date(value);
  else if (typeof value === "number" && Number.isFinite(value)) {
    date = new Date(value);
  }
  return date && !Number.isNaN(date.getTime()) ? date : null;
}

const FILE_DATE_FORMAT = new Intl.DateTimeFormat("en-AU", {
  timeZone: "Australia/Sydney",
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
});

// The screen's Sydney calendar date as YYYY-MM-DD, or null.
function sydneyIsoDate(value) {
  const date = toValidDate(value);
  if (!date) return null;
  const parts = {};
  FILE_DATE_FORMAT.formatToParts(date).forEach(({ type, value: part }) => {
    parts[type] = part;
  });
  return `${parts.year}-${parts.month}-${parts.day}`;
}

/**
 * The downloaded report's file name: the check, the screened name (the row id
 * when the name has no usable characters) and the Sydney screen date, e.g.
 * "pep-sanctions-screening-Jane-Citizen-2026-10-02.pdf". It never holds the
 * DOB.
 */
export function screeningPdfFilename(row, check) {
  const prefix =
    typeof check === "string" &&
    Object.prototype.hasOwnProperty.call(FILENAME_PREFIXES, check)
      ? FILENAME_PREFIXES[check]
      : "screening";
  const name = `${row?.first_name || ""} ${row?.last_name || ""}`.trim();
  const base =
    sanitiseFilenamePart(name) || sanitiseFilenamePart(row?.id) || "report";
  const date = sydneyIsoDate(row?.created_at);
  return `${prefix}-${base}${date ? `-${date}` : ""}.pdf`;
}
